import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Switch } from '@mui/material';
import { type GridColDef, type GridRenderCellParams, type GridSortModel } from '@mui/x-data-grid';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import {
  type FirmwareRelease,
  type FirmwareReleasePackage,
  type FirmwareReleaseSortableField,
  SortOrder,
} from 'api/admin/firmware/firmware';
import { useArchiveFirmwareReleases } from 'api/queries/useArchiveFirmwareReleases';
import { useFirmwareReleases } from 'api/queries/useFirmwareReleases';
import './FirmwareManagerTable.scss';

const DEFAULT_PAGE_SIZE = 20;

const SORTABLE_FIELDS = new Set<FirmwareReleaseSortableField>([
  'version',
  'deviceType',
  'createdAt',
  'numberOfDevices',
]);

const renderPackages = (params: GridRenderCellParams<FirmwareRelease>) => {
  const packages = (params.value ?? []) as FirmwareReleasePackage[];

  if (!packages.length) {
    return <span>—</span>;
  }

  return (
    <div className="firmware-packages-cell">
      {packages.map((pkg) => (
        <span key={`${pkg.name}-${pkg.version}`}>{`${pkg.name} ${pkg.version}`}</span>
      ))}
    </div>
  );
};

const renderNotes = (params: GridRenderCellParams<FirmwareRelease>) => {
  const description = params.value as string | null;

  if (!description) {
    return <span>—</span>;
  }

  return (
    <span className="firmware-cell-ellipsis" title={description}>
      {description}
    </span>
  );
};

const COLUMNS: GridColDef[] = [
  { headerName: 'Release', field: 'version', flex: 0.8 },
  { headerName: 'Device Compatibility', field: 'deviceType', flex: 0.9 },
  {
    headerName: 'Packages',
    field: 'packages',
    flex: 1.4,
    sortable: false,
    renderCell: renderPackages,
  },
  { headerName: 'Date Created', field: 'createdAt', flex: 0.8 },
  { headerName: 'Effective Devices', field: 'numberOfDevices', flex: 0.8 },
  {
    headerName: 'Notes',
    field: 'description',
    flex: 1.4,
    sortable: false,
    renderCell: renderNotes,
  },
];

const FirmwareManagerTable: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<Array<number | string>>([]);

  const sortBy = useMemo<FirmwareReleaseSortableField | undefined>(() => {
    const field = sortModel[0]?.field as FirmwareReleaseSortableField | undefined;
    return field && SORTABLE_FIELDS.has(field) ? field : undefined;
  }, [sortModel]);

  const sortOrder = useMemo<SortOrder | undefined>(() => {
    const sortDirection = sortModel[0]?.sort;
    if (!sortDirection) {
      return undefined;
    }
    return sortDirection as SortOrder;
  }, [sortModel]);

  const { data, isLoading, isError, refetch } = useFirmwareReleases({
    page: pageNumber + 1,
    limit: DEFAULT_PAGE_SIZE,
    includeArchived,
    sortBy,
    sortOrder,
  });

  const archiveMutation = useArchiveFirmwareReleases({
    successCallback: () => {
      refetch();
      setSelectedRowIds([]);
    },
  });

  const releases = data?.releases ?? [];
  const total = data?.total ?? 0;

  const selectedReleases = useMemo(
    () => releases.filter((release) => selectedRowIds.includes(release.id)),
    [releases, selectedRowIds],
  );

  const canArchive =
    selectedReleases.length > 0 && selectedReleases.every((release) => !release.isArchived);
  const canUnarchive =
    selectedReleases.length > 0 && selectedReleases.every((release) => release.isArchived);

  const handleSetArchived = (archived: boolean) => {
    archiveMutation.mutate({
      releaseIds: selectedRowIds.map(Number),
      archived,
    });
  };

  const handlePageChange = ({ page }: { page: number }) => {
    setPageNumber(page);
    setSelectedRowIds([]);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setPageNumber(0);
    setSelectedRowIds([]);

    if (!model.length) {
      setSortModel([]);
      return;
    }

    const [sortItem] = model;

    setSortModel([sortItem]);
  };

  return (
    <div className="firmware-manager">
      {isError && (
        <Alert severity="error" className="firmware-error-alert">
          Failed to load firmware releases.
        </Alert>
      )}

      <Box className="firmware-actions-row">
        <Box className="firmware-actions">
          <Button
            variant="contained"
            color="primary"
            disabled={!canArchive || archiveMutation.isLoading}
            onClick={() => handleSetArchived(true)}
          >
            Archive
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={!canUnarchive || archiveMutation.isLoading}
            onClick={() => handleSetArchived(false)}
          >
            Unarchive
          </Button>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={includeArchived}
              onChange={(event) => {
                setPageNumber(0);
                setSelectedRowIds([]);
                setIncludeArchived(event.target.checked);
              }}
            />
          }
          label="Include archived"
        />
      </Box>

      <CentsDataGridServer
        tableName="FIRMWARE_RELEASES"
        data={releases}
        columns={COLUMNS}
        isLoading={isLoading}
        rowCount={total}
        pageSize={DEFAULT_PAGE_SIZE}
        page={pageNumber}
        onPageChange={handlePageChange}
        checkboxSelection
        rowSelectionModel={selectedRowIds}
        onRowSelectionModelChange={(newSelection: Array<number | string>) => {
          setSelectedRowIds(newSelection);
        }}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        getRowHeight={() => 'auto'}
        getRowClassName={({ row }: { row: FirmwareRelease }) =>
          row.isArchived ? 'firmware-row-archived' : ''
        }
      />
    </div>
  );
};

export default FirmwareManagerTable;
